import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Link,
  CardMedia,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Science,
  Group,
  Assessment,
  Share,
  Download,
  Upload,
  Info,
  CheckCircle,
  Warning,
  School,
  Public,
  DataUsage,
  Timeline,
  Visibility,
  VisibilityOff,
  HomeWork,
  Hive,
  Sensors,
  ExpandMore,
  Launch,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  People,
  TrendingUp,
  BarChart,
  PieChart,
  Cancel
} from '@mui/icons-material';
import { ApiService } from '@api/index';
import { useAppSelector } from '@hooks/redux';

interface ResearchProject {
  id: number;
  name: string;
  description: string;
  institution: string;
  website?: string;
  start_date: string;
  end_date?: string;
  status: 'recruiting' | 'active' | 'completed';
  participant_count: number;
  required_data_types: string[];
  optional_data_types: string[];
  contact_email?: string;
  user_participation: any;
}

interface UserData {
  apiaries: number;
  hives: number;
  devices: number;
  measurements: number;
}

interface Statistics {
  apiaries: number;
  hives: number;
  devices: number;
  measurements: number;
}

const EnhancedResearch: React.FC = () => {
  const { apiaries, hives } = useAppSelector((state) => state.locations)
  const { devices } = useAppSelector((state) => state.devices)
  const [projects, setProjects] = useState<ResearchProject[]>([]);
  const [userData, setUserData] = useState<UserData>({
    apiaries: 0,
    hives: 0,
    devices: 0,
    measurements: 0
  });
  const [loading, setLoading] = useState(true);
  const [participationDialog, setParticipationDialog] = useState<{
    open: boolean;
    project: ResearchProject | null;
  }>({ open: false, project: null });
  const [dataContribution, setDataContribution] = useState({
    shareHiveData: true,
    shareInspectionData: true,
    shareMeasurementData: false,
    shareLocationData: false
  });

  useEffect(() => {
    loadResearchData();
  }, []);

  useEffect(() => {
    setUserData((prev) => ({
      ...prev,
      apiaries: apiaries.length,
      hives: hives.length,
      devices: devices.length,
    }))
  }, [apiaries.length, hives.length, devices.length])

  const loadResearchData = async () => {
    try {
      setLoading(true);

      // Load research projects from backend using ApiService
      const [projectsResponse, statsResponse] = await Promise.all([
        ApiService.get('research/projects/'),
        ApiService.get('research/statistics/')
      ]);

      const projectsData = projectsResponse.data;
      if (Array.isArray(projectsData)) {
        setProjects(projectsData);
      } else if (projectsData && Array.isArray(projectsData.data)) {
        setProjects(projectsData.data);
      } else if (projectsData && Array.isArray(projectsData.results)) {
        setProjects(projectsData.results);
      } else {
        setProjects([]);
      }
      const stats = statsResponse.data?.data || statsResponse.data || {}
      setUserData({
        apiaries: apiaries.length,
        hives: hives.length,
        devices: devices.length,
        measurements: stats.measurements || stats.data_points_collected || 0,
      });
    } catch (error) {
      console.error('Failed to load research data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinProject = (project: ResearchProject) => {
    setParticipationDialog({ open: true, project });
  };

  const confirmParticipation = async () => {
    if (!participationDialog.project) return;

    try {
      const response = await ApiService.post('research/participations/', {
        project: participationDialog.project.id,
        data_sharing_preferences: dataContribution
      });

      setProjects(prev => prev.map(p =>
        p.id === participationDialog.project!.id
          ? { ...p, user_participation: response.data, participant_count: p.participant_count + 1 }
          : p
      ));

      setParticipationDialog({ open: false, project: null });
    } catch (error) {
      console.error('Failed to join project:', error);
    }
  };

  const handleLeaveProject = async (projectId: number) => {
    try {
      await ApiService.delete(`research/participations/${projectId}/`);

      setProjects(prev => prev.map(p =>
        p.id === projectId
          ? { ...p, user_participation: null, participant_count: p.participant_count - 1 }
          : p
      ));
    } catch (error) {
      console.error('Failed to leave project:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'recruiting': return 'info.main';
      case 'active': return 'success.main';
      case 'completed': return 'text.disabled';
      default: return 'text.secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'recruiting': return 'Recruiting';
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} sx={{ color: 'primary.main' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <Science sx={{ mr: 2, color: 'primary.main' }} />
          Research Participation
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Contribute to bee research and help advance our understanding of bee health and behavior
        </Typography>
      </Box>

      {/* Warning Alert */}
      <Alert severity="warning" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Research Data Sharing:</strong> By participating in research projects, you agree to share
          anonymized data from your hives and devices. Your personal information will never be shared without consent.
        </Typography>
      </Alert>

      {/* User Data Overview */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <Assessment sx={{ mr: 1 }} />
          My Smart Nyuki Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Overview of your beekeeping data available for research contribution
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <HomeWork sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5">{userData.apiaries}</Typography>
              <Typography variant="body2" color="text.secondary">
                Locations
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Hive sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h5">{userData.hives}</Typography>
              <Typography variant="body2" color="text.secondary">
                Hives
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Sensors sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h5">{userData.devices}</Typography>
              <Typography variant="body2" color="text.secondary">
                Devices
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <BarChart sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h5">{(userData.measurements || 0).toLocaleString()}</Typography>
              <Typography variant="body2" color="text.secondary">
                Measurements
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Research Projects */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3 }}>
        Available Research Projects
      </Typography>

      <Grid container spacing={3}>
        {projects.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                No research projects yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When a university or association publishes a study, it will appear here so you can share anonymized hive data.
              </Typography>
            </Paper>
          </Grid>
        )}
        {projects.map((project) => (
          <Grid item xs={12} key={project.id}>
            <Card sx={{
              border: project.user_participation ? '2px solid' : '1px solid',
              borderColor: project.user_participation ? 'success.main' : 'divider',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)'
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
                    {/* Image removed as backend does not provide it yet */}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {project.name}
                        {project.user_participation && (
                          <CheckCircle sx={{ ml: 1, color: 'success.main', fontSize: 20 }} />
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {project.description}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={getStatusLabel(project.status)}
                    sx={{
                      backgroundColor: getStatusColor(project.status),
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Institution:</strong> {project.institution}
                    </Typography>
                    {project.website && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Website:</strong>{' '}
                        <Link href={project.website} target="_blank" rel="noopener">
                          {project.website}
                          <Launch sx={{ fontSize: 14, ml: 0.5 }} />
                        </Link>
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      <strong>Participants:</strong> {(project.participant_count || 0).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Start Date:</strong> {project.start_date ? new Date(project.start_date).toLocaleDateString() : 'N/A'}
                    </Typography>
                    {project.end_date && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>End Date:</strong> {new Date(project.end_date).toLocaleDateString()}
                      </Typography>
                    )}
                    {project.contact_email && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Contact:</strong>{' '}
                        <Link href={`mailto:${project.contact_email}`}>
                          {project.contact_email}
                        </Link>
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2">
                      Requirements & Data Types
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Requirements:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {project.required_data_types?.map((req, index) => (
                            <Chip key={index} label={req} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Data Types:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {project.optional_data_types?.map((type, index) => (
                            <Chip key={index} label={type} size="small" color="primary" />
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </CardContent>

              <CardActions sx={{ px: 3, pb: 3 }}>
                {project.user_participation ? (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleLeaveProject(project.id)}
                    startIcon={<Cancel />}
                  >
                    Leave Project
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => handleJoinProject(project)}
                    startIcon={<Science />}
                    sx={{
                      backgroundColor: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.dark' }
                    }}
                    disabled={project.status === 'completed'}
                  >
                    Join Research
                  </Button>
                )}
                {project.website && (
                  <Button
                    variant="outlined"
                    href={project.website}
                    target="_blank"
                    startIcon={<Launch />}
                    sx={{ ml: 1 }}
                  >
                    Learn More
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Participation Dialog */}
      <Dialog
        open={participationDialog.open}
        onClose={() => setParticipationDialog({ open: false, project: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Join Research Project
        </DialogTitle>
        <DialogContent>
          {participationDialog.project && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {participationDialog.project.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {participationDialog.project.description}
              </Typography>

              <Alert severity="info" sx={{ mb: 3 }}>
                By joining this research project, you agree to share the selected data types
                with the research institution. All data is anonymized and used solely for research purposes.
              </Alert>

              <Typography variant="subtitle1" gutterBottom>
                Data Sharing Preferences:
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <Hive />
                  </ListItemIcon>
                  <ListItemText primary="Hive Data" secondary="Basic hive information and status" />
                  <Switch
                    checked={dataContribution.shareHiveData}
                    onChange={(e) => setDataContribution(prev => ({
                      ...prev,
                      shareHiveData: e.target.checked
                    }))}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Visibility />
                  </ListItemIcon>
                  <ListItemText primary="Inspection Data" secondary="Inspection results and observations" />
                  <Switch
                    checked={dataContribution.shareInspectionData}
                    onChange={(e) => setDataContribution(prev => ({
                      ...prev,
                      shareInspectionData: e.target.checked
                    }))}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Sensors />
                  </ListItemIcon>
                  <ListItemText primary="Measurement Data" secondary="Sensor readings and environmental data" />
                  <Switch
                    checked={dataContribution.shareMeasurementData}
                    onChange={(e) => setDataContribution(prev => ({
                      ...prev,
                      shareMeasurementData: e.target.checked
                    }))}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText primary="Location Data" secondary="General geographic location (anonymized)" />
                  <Switch
                    checked={dataContribution.shareLocationData}
                    onChange={(e) => setDataContribution(prev => ({
                      ...prev,
                      shareLocationData: e.target.checked
                    }))}
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParticipationDialog({ open: false, project: null })}>
            Cancel
          </Button>
          <Button
            onClick={confirmParticipation}
            variant="contained"
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' }
            }}
          >
            Join Project
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EnhancedResearch;
